import React, {FC, useEffect, useState} from "react"

import topupService from "@irati/wallet/services/TopUp"
import paymentService from "@irati/payments/services/Payments"
import Loading from "@irati/ui/src/Loading"
import {Topup} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {Body, H1, H3} from "@irati/ui/src/Typography"
import authentication from "@irati/auth"
import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import {FormattedDate, FormattedRelative, FormattedMessage, FormattedCurrency} from "@irati/utils/lang"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {RouteComponentProps} from "react-router-dom"
import Markdown from "@irati/ui/src/Markdown"

interface Props extends RouteComponentProps<{topUpId: string}> {}

const AdminRewardTopUpSuccessContainer: FC<Props> = ({match}) => {
  const [hasFailed, setHasFailed] = useState(false)
  const [load, setLoad] = useState<Promise<any>>()
  const [topUp, setTopUp] = useState<Topup>()

  useEffect(() => {
    const {topUpId} = match.params
    if (topUpId) {
      // Redirect after successful payment will land here and get the topUp just created
      const req = topupService.get(topUpId)
      setLoad(req)
      req.then((res) => {
        return paymentService.completePayment({paymentOrderId: res.paymentOrder.uuid}).then((payment) => {
          if (payment.status === "FAILED") {
            setHasFailed(true)
          } else {
            return topupService.get(topUpId, {fetchPolicy: "network-only"}).then((completedTopUp) => {
              setTopUp(completedTopUp)
            })
          }
        })
      })
    }
  }, [])

  return (
    <Loading load={load} initLoad>
      {hasFailed && (
        <Card marginTop={4}>
          <H1 marginBottom={2} textAlign="center">
            <FormattedMessage id="wallet.reward.admin.confirmationModal.error.title" />
          </H1>

          <Markdown source="wallet.reward.admin.confirmationModal.error.body" />

          <ButtonRouterLink primary full to="/flexi-reward" marginTop={2}>
            <FormattedMessage id="wallet.reward.admin.confirmationModal.btn.backToRewardWallet" />
          </ButtonRouterLink>
        </Card>
      )}
      {topUp && (
        <Card marginTop={4}>
          <Flex direction="column" alignItems="center" marginBottom={4}>
            <H1 marginBottom={2}>
              <FormattedMessage id="wallet.reward.admin.confirmationModal.title" />
            </H1>
            <H3 marginLeft={0.5}>
              <Flex alignItems="center">
                <FormattedCurrency value={topUp?.flexiPointAmount} currency={topUp?.flexiPointCurrency} />
                &nbsp;
                <FormattedMessage id="wallet.reward.admin.confirmationModal.points" />
              </Flex>
            </H3>
          </Flex>
          <Body marginBottom={3}>
            <FormattedMessage id="wallet.reward.admin.confirmationModal.body.thankYouMessage" />
          </Body>
          <Body marginBottom={3}>
            <FormattedMessage
              id="wallet.reward.admin.confirmationModal.body.userEmail"
              values={{userEmail: authentication.user().email}}
            />
          </Body>
          <Card bgColor={theme.colors.greys.lightest}>
            <Flex justifyContent="space-between" marginBottom={2}>
              <Body>
                <FormattedMessage id="wallet.reward.admin.confirmationModal.rewardPointsPurchased" />
              </Body>
              <Body color={theme.colors.greys.black} weight="bold">
                <FormattedCurrency value={topUp?.flexiPointAmount} />
              </Body>
            </Flex>

            <Flex justifyContent="space-between" marginBottom={2}>
              <Body>
                <FormattedMessage id="wallet.tenant.topUp.cardPopUp.order" />
              </Body>
              <Body color={theme.colors.greys.black} weight="bold">
                {topUp?.uuid}
              </Body>
            </Flex>

            <Flex justifyContent="space-between" marginBottom={2}>
              <Body>
                <FormattedMessage id="wallet.tenant.topUp.invoicePopUp.date" />
              </Body>
              <Body color={theme.colors.greys.black} weight="bold">
                <FormattedDate day="numeric" month="long" year="numeric" value={topUp?.metadata?.createdAt} />
              </Body>
            </Flex>

            <Flex justifyContent="space-between" marginBottom={2}>
              <Body>
                <FormattedMessage id="wallet.tenant.topUp.invoicePopUp.timeAdded" />
              </Body>
              <Body color={theme.colors.greys.black} weight="bold">
                <FormattedRelative value={topUp?.metadata?.createdAt} />
              </Body>
            </Flex>

            <Flex justifyContent="space-between">
              <Body>
                <FormattedMessage id="wallet.reward.admin.confirmationModal.totalPaid" />
              </Body>
              <Body color={theme.colors.greys.black} weight="bold">
                {formatCurrency({
                  currencyCode: topUp?.paymentOrder?.currency,
                  value: topUp?.paymentOrder?.amount
                })}
              </Body>
            </Flex>
          </Card>
          <ButtonRouterLink primary full to="/flexi-reward" marginTop={2}>
            <FormattedMessage id="wallet.reward.admin.confirmationModal.btn.backToRewardWallet" />
          </ButtonRouterLink>
        </Card>
      )}
    </Loading>
  )
}

export default AdminRewardTopUpSuccessContainer
